import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import { platform } from '@/shared/api/resources';
import { formatDateTime, formatMoney } from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { DataTable, type Column } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { PageHeader } from '@/shared/ui/PageHeader';
import { Select } from '@/shared/ui/Select';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { creditConsumption, type BureauPrice, type BureauProduct } from './api';
import { ConsultationLimitsSection } from './ConsultationLimitsSection';
import { ConsumptionReportView } from './ConsumptionReportView';
import { centavosDoTexto, produtos, reaisDeCentavos, rotuloDoProduto } from './labels';

/**
 * Limites, precos e consumo das consultas ao bureau, na administracao da plataforma.
 *
 * O contrato com a Vigidata e da plataforma: o preco de cada produto e um so, para
 * todas as empresas. A tabela nasce vazia, porque o preco nao esta documentado, e sem
 * preco o custo de cada chamada fica desconhecido. Cada mudanca e uma versao nova, com
 * vigencia; a revogacao vale dali em diante. Tudo vai para a trilha da plataforma.
 */
export function PlatformCreditConsumptionPage() {
  const [empresa, setEmpresa] = useState('');
  const empresas = useQuery({
    queryKey: ['platform', 'tenants', 'consumo'],
    queryFn: ({ signal }) => platform.listTenants({ limit: 100 }, signal),
  });

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Consultas ao bureau: limites, precos e consumo"
        description="Quantas consultas cada empresa, corretor e tomador pode fazer, o preco de cada produto da SeguraCoreCredit, mantido pela plataforma, e o consumo de todas as empresas."
      />

      <ConsultationLimitsSection empresas={empresas.data?.items ?? []} />

      <PricesSection />

      <section className="flex flex-col gap-3">
        <h3 className="text-base font-semibold text-ink-900">
          Consumo de todas as empresas
        </h3>
        <ConsumptionReportView
          chave="plataforma"
          comEmpresa
          carregar={(filtros, signal) =>
            creditConsumption.platformReport(filtros, signal)
          }
          extraFiltros={{
            filtros: empresa ? { tenant_id: empresa } : {},
            campos: (
              <Select
                label="Empresa"
                placeholder="Todas"
                value={empresa}
                options={(empresas.data?.items ?? []).map((e) => ({
                  value: e.id,
                  label: e.name,
                }))}
                onChange={(evento) => setEmpresa(evento.target.value)}
              />
            ),
          }}
        />
      </section>
    </div>
  );
}

function PricesSection() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [produto, setProduto] = useState<BureauProduct>('analise_credito_cnpj');
  const [valor, setValor] = useState('');
  const [vigencia, setVigencia] = useState('');
  const [origem, setOrigem] = useState('');
  const [erroDoValor, setErroDoValor] = useState<string | undefined>();
  const [revogar, setRevogar] = useState<BureauPrice | null>(null);

  const precos = useQuery({
    queryKey: ['platform', 'precos-do-bureau'],
    queryFn: ({ signal }) => creditConsumption.listPrices(signal),
  });

  const atualizar = () =>
    void queryClient.invalidateQueries({ queryKey: ['platform', 'precos-do-bureau'] });

  const cadastrar = useMutation({
    mutationFn: (centavos: number) =>
      creditConsumption.createPrice({
        product: produto,
        price: reaisDeCentavos(centavos),
        notes: origem,
        ...(vigencia ? { effective_from: new Date(vigencia).toISOString() } : {}),
      }),
    onSuccess: () => {
      toast.success('Versao de preco cadastrada.');
      setValor('');
      setVigencia('');
      setOrigem('');
      atualizar();
    },
    onError: (erro) =>
      toast.error(
        erro instanceof ApiError ? erro.message : 'Nao foi possivel cadastrar.',
      ),
  });

  const enviar = (evento: FormEvent) => {
    evento.preventDefault();
    const centavos = centavosDoTexto(valor);
    if (centavos === null) {
      setErroDoValor('Informe o valor em reais, com ate duas casas decimais.');
      return;
    }
    setErroDoValor(undefined);
    cadastrar.mutate(centavos);
  };

  const vigentes = new Map<BureauProduct, string>();
  for (const preco of precos.data ?? []) {
    if (!preco.active_now) continue;
    const atual = vigentes.get(preco.product);
    const daVez = (precos.data ?? []).find((p) => p.id === atual);
    if (!daVez || daVez.effective_from < preco.effective_from) {
      vigentes.set(preco.product, preco.id);
    }
  }

  const colunas: Column<BureauPrice>[] = [
    {
      key: 'produto',
      header: 'Produto',
      render: (preco) => rotuloDoProduto[preco.product] ?? preco.product,
    },
    {
      key: 'preco',
      header: 'Preco',
      align: 'right',
      render: (preco) => (
        <span className="tabular-nums">{formatMoney(preco.price)}</span>
      ),
    },
    {
      key: 'vigencia',
      header: 'Vigencia',
      render: (preco) => (
        <span className="tabular-nums">{formatDateTime(preco.effective_from)}</span>
      ),
    },
    {
      key: 'situacao',
      header: 'Situacao',
      render: (preco) =>
        preco.revoked_at ? (
          <Badge tone="neutral">Revogada</Badge>
        ) : preco.scheduled ? (
          <Badge tone="info">Agendada</Badge>
        ) : vigentes.get(preco.product) === preco.id ? (
          <Badge tone="success">Vigente</Badge>
        ) : (
          <Badge tone="neutral">Substituida</Badge>
        ),
    },
    {
      key: 'origem',
      header: 'Origem',
      hideBelow: 'md',
      render: (preco) => (
        <div className="min-w-0">
          <p className="truncate text-ink-900">{preco.notes}</p>
          <p className="truncate text-2xs text-ink-500">{preco.created_label}</p>
        </div>
      ),
    },
    {
      key: 'acao',
      header: 'Acao',
      align: 'right',
      render: (preco) =>
        preco.revoked_at ? null : (
          <Button
            size="sm"
            variant="secondary"
            aria-label={`Revogar o preco de ${rotuloDoProduto[preco.product]}`}
            onClick={() => setRevogar(preco)}
          >
            Revogar
          </Button>
        ),
    },
  ];

  return (
    <Card>
      <CardHeader
        title="Precos dos produtos"
        description="Sem preco, o custo da chamada fica desconhecido. Nenhum valor e cadastrado sem a origem."
      />
      <CardBody className="flex flex-col gap-4">
        <form
          className="grid gap-3 md:grid-cols-4"
          onSubmit={enviar}
          aria-label="Nova versao de preco"
        >
          <Select
            label="Produto"
            value={produto}
            options={produtos.map((p) => ({ value: p, label: rotuloDoProduto[p] }))}
            onChange={(evento) => setProduto(evento.target.value as BureauProduct)}
          />
          <MoneyField
            label="Preco por consulta (R$)"
            value={valor}
            error={erroDoValor}
            onValueChange={setValor}
          />
          <Field
            label="Vigente a partir de"
            type="datetime-local"
            hint="Em branco, agora. Nunca no passado."
            value={vigencia}
            onChange={(evento) => setVigencia(evento.target.value)}
          />
          <div className="md:col-span-4">
            <Textarea
              label="Origem do preco"
              hint="Contrato, aditivo ou proposta de onde o valor saiu."
              value={origem}
              onChange={(evento) => setOrigem(evento.target.value)}
            />
          </div>
          <div className="md:col-span-4">
            <Button
              type="submit"
              loading={cadastrar.isPending}
              disabled={!valor.trim() || !origem.trim()}
            >
              Cadastrar versao
            </Button>
          </div>
        </form>

        {precos.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar os precos">
            {precos.error.message}
          </Alert>
        ) : null}
        <DataTable
          columns={colunas}
          items={precos.data ?? []}
          keyOf={(preco) => preco.id}
          loading={precos.isPending}
          empty={
            <EmptyState
              icon="coins"
              title="Nenhum preco cadastrado"
              description="Enquanto nao houver preco, o custo de cada chamada fica desconhecido no relatorio."
            />
          }
        />
      </CardBody>

      {revogar ? (
        <RevocationModal
          preco={revogar}
          onClose={() => setRevogar(null)}
          onDone={() => {
            setRevogar(null);
            atualizar();
          }}
        />
      ) : null}
    </Card>
  );
}

function RevocationModal({
  preco,
  onClose,
  onDone,
}: {
  preco: BureauPrice;
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [motivo, setMotivo] = useState('');
  const revogar = useMutation({
    mutationFn: () => creditConsumption.revokePrice(preco.id, motivo),
    onSuccess: () => {
      toast.success('Versao de preco revogada.');
      onDone();
    },
    onError: (erro) =>
      toast.error(
        erro instanceof ApiError ? erro.message : 'Nao foi possivel revogar.',
      ),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`Revogar o preco de ${rotuloDoProduto[preco.product]}`}
      description="A revogacao vale a partir de agora. As chamadas anteriores continuam com o custo gravado."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            loading={revogar.isPending}
            disabled={!motivo.trim()}
            onClick={() => revogar.mutate()}
          >
            Revogar
          </Button>
        </>
      }
    >
      <Textarea
        label="Motivo da revogacao"
        value={motivo}
        onChange={(evento) => setMotivo(evento.target.value)}
      />
    </Modal>
  );
}
